import { Panel, RoutedPanelProps } from '@/components/Layout/Panel/Panel';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useCreateMaterialMutation,
  useMaterialEntry,
  useUpdateMaterialMutation,
  useWorkorder,
  useWorkorderLock,
} from '@/features/workorders/workorders.hooks';
import { Unit } from '@certhon/models';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';

export const MaterialDetailPanel = ({
  params,
  ...panelProps
}: RoutedPanelProps) => {
  const { workorderId, materialId } = params;
  const workorderRecnum = parseInt(workorderId);
  const isNew = materialId === 'new';

  const materialQuery = useMaterialEntry(isNew ? 0 : parseInt(materialId));
  const workorderQuery = useWorkorder(workorderRecnum);
  const { locked: workorderLocked, reason: lockReason } = useWorkorderLock(
    workorderQuery.data,
  );
  const createMutation = useCreateMaterialMutation(workorderRecnum);
  const updateMutation = useUpdateMaterialMutation(workorderRecnum);

  // Form state - default to 1 pc for new entries
  const [text, setText] = useState('');
  const [quantity, setQuantity] = useState<number | ''>(isNew ? 1 : '');
  const [unit, setUnit] = useState<Unit | ''>(isNew ? 'pc' : '');

  // Load existing data
  useEffect(() => {
    if (!isNew && materialQuery.data) {
      const entry = materialQuery.data;
      setText(entry.text ?? '');
      setQuantity(entry.quantity ?? '');
      setUnit(entry.unit ?? '');
    }
  }, [isNew, materialQuery.data]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isNew) {
      await createMutation.mutateAsync({
        workorder_id: workorderRecnum,
        text: text || null,
        quantity: quantity === '' ? null : quantity,
        unit: unit || null,
      });
    } else {
      await updateMutation.mutateAsync({
        recnum: parseInt(materialId),
        data: {
          text: text || null,
          quantity: quantity === '' ? null : quantity,
          unit: unit || null,
        },
      });
    }

    window.history.back();
  };

  const isLoading =
    (!isNew && materialQuery.isLoading) || workorderQuery.isLoading;
  const isSaving = createMutation.isPending || updateMutation.isPending;
  const isLocked =
    workorderLocked ||
    (!isNew &&
      (materialQuery.data?.locked || materialQuery.data?.source === 'pre'));

  const title = isNew ? 'Add Material' : 'Edit Material';

  return (
    <Panel {...panelProps} title={title}>
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : isLocked ? (
        <div className="text-muted-foreground py-8 text-center">
          {lockReason ??
            (materialQuery.data?.source === 'pre'
              ? 'Pre-configured materials cannot be edited.'
              : 'This material is locked and cannot be edited.')}
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="text">Article Name</Label>
            <Input
              id="text"
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="e.g., Copper pipe 15mm"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="quantity">Quantity</Label>
              <Input
                id="quantity"
                type="number"
                min="0"
                step="1"
                value={quantity}
                onChange={(e) =>
                  setQuantity(
                    e.target.value === '' ? '' : parseInt(e.target.value),
                  )
                }
                placeholder="e.g., 5"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="unit">Unit</Label>
              <Select
                value={unit}
                onValueChange={(value) => setUnit(value as Unit)}
              >
                <SelectTrigger id="unit">
                  <SelectValue placeholder="Select unit" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pc">pc (pieces)</SelectItem>
                  <SelectItem value="m">m (meters)</SelectItem>
                  <SelectItem value="l">l (liters)</SelectItem>
                  <SelectItem value="kg">kg (kilograms)</SelectItem>
                  <SelectItem value="m²">m² (square meters)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex gap-2">
            <Button type="submit" disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => window.history.back()}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
    </Panel>
  );
};
